// Copyright (c) 2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { collection, query, where, getDocs, onSnapshot, doc, getDoc } from 'firebase/firestore';

import { DELIVERY_BALES_COLLECTION_PATH, AVAILABLE_DELIVERY_ANALYTICS_DOCUMENT_PATH } from './FirebaseUtils';

export async function getDeliveryBales(firestore, clientId, deliveryId, importedOnly = false) {
  /**
   * Get the bales for a given delivery from firestore
   * @param {Firestore} firestore - The firestore instance
   * @param {string} clientId - The client id
   * @param {string} deliveryId - The delivery id
   * @param {boolean} importedOnly - Whether to only get imported bales
   * @returns {Promise<Object>} The bales
   */
  let q = query(collection(firestore, DELIVERY_BALES_COLLECTION_PATH(clientId, deliveryId)));
  if (importedOnly) {
    q = query(q, where('imported', '==', true));
  }
  const snapshot = await getDocs(q);
  return snapshot.docs.reduce((acc, doc) => {
    acc[doc.id] = doc.data();
    return acc;
  }, {});
}

export function subscribeToDeliveryBales(firestore, clientId, deliveryId, onUpdate, importedOnly = false) {
  /**
   * Subscribe to the bales for a given delivery from firestore
   * @param {Firestore} firestore - The firestore instance
   * @param {string} clientId - The client id
   * @param {string} deliveryId - The delivery id
   * @param {function} onUpdate - The function to call when the bales change
   * @param {boolean} importedOnly - Whether to only get imported bales
   */
  let q = query(collection(firestore, DELIVERY_BALES_COLLECTION_PATH(clientId, deliveryId)));
  if (importedOnly) {
    q = query(q, where('imported', '==', true));
  }
  const unsub = onSnapshot(q, (snapshot) => {
    const importedBales = snapshot.docs.reduce((acc, doc) => {
      acc[doc.id] = doc.data();
      return acc;
    }, {});
    onUpdate(importedBales);
  });
  return unsub;
}

/**
 * Retrieves available delivery analytics for a specific client from Firestore.
 * -> Shows for which labelsets there are available analytics
 * @async
 * @param {Object} firestore - The Firestore instance
 * @param {string} clientId - The unique identifier of the client
 * @returns {Promise<Object>} The available delivery analytics object, or an empty object if not found
 */
export async function getAvailableDeliveryAnalytics(firestore, clientId) {
  const availableDeliveryAnalyticsRef = doc(firestore, AVAILABLE_DELIVERY_ANALYTICS_DOCUMENT_PATH(clientId));
  const availableDeliveryAnalyticsSnapshot = await getDoc(availableDeliveryAnalyticsRef);

  if (!availableDeliveryAnalyticsSnapshot.exists()) {
    return {};
  }

  const availableDeliveryAnalytics = availableDeliveryAnalyticsSnapshot.data();
  if (!availableDeliveryAnalytics?.available_labelsets) {
    return {};
  }

  return availableDeliveryAnalytics.available_labelsets;
}
